/**
 * Envío ER-2 · Red de seguridad de toda la aplicación.
 *
 * Sin esto, un error al dibujar cualquier pantalla desarma la app entera y
 * queda sólo el fondo oscuro («pantalla negra»). Con esto aparece un aviso
 * con «Recargar» y «Enviar reporte»; el reporte llega a Admin → Reportes con
 * el error y la pantalla donde pasó.
 */
import { Component } from 'react';
import { learningService } from '../services/learningService';
import { feedbackContext } from './learning/learningModel';
import { useWorkflowStore } from '../store/workflowStore';

const box = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 24, background: '#0e0e12', color: '#eef0f4',
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
};
const card = {
  maxWidth: 460, width: '100%', padding: 28, borderRadius: 20, background: '#15161c',
  border: '1px solid rgba(255,255,255,.08)', display: 'flex', flexDirection: 'column', gap: 14,
};
const btn = (primary) => ({
  minHeight: 44, padding: '0 20px', borderRadius: 12, font: 'inherit', fontWeight: 700, cursor: 'pointer',
  border: primary ? 0 : '1px solid rgba(255,255,255,.18)',
  background: primary ? '#36d39a' : 'transparent', color: primary ? '#0d2a20' : '#eef0f4',
});

export function crashReport(error, componentStack) {
  const msg = (error && (error.message || String(error))) || 'Error desconocido';
  const stack = String(componentStack || '').trim().split('\n').slice(0, 8).join('\n');
  return `Pantalla de error automática.\nError: ${msg}\n${stack}`.slice(0, 3900);
}

export default class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stack: '', sent: 'idle' };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ stack: info?.componentStack || '' });
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info?.componentStack);
  }

  sendReport = async () => {
    if (this.state.sent === 'sending' || this.state.sent === 'ok') return;
    this.setState({ sent: 'sending' });
    try {
      let projectId; let projectName;
      try {
        const wf = useWorkflowStore.getState();
        projectId = wf.currentProjectId; projectName = wf.projectName;
      } catch { /* store no disponible */ }
      await learningService.sendFeedback({
        kind: 'problem',
        message: crashReport(this.state.error, this.state.stack),
        context: feedbackContext({ projectId, projectName, view: 'error' }),
      });
      this.setState({ sent: 'ok' });
    } catch {
      this.setState({ sent: 'fail' });
    }
  };

  render() {
    const { error, sent } = this.state;
    if (!error) return this.props.children;
    return (
      <div style={box} role="alert" data-testid="app-error-boundary">
        <div style={card}>
          <h1 style={{ margin: 0, fontSize: 22 }}>Algo salió mal en esta pantalla</h1>
          <p style={{ margin: 0, color: '#b4b9c4', lineHeight: 1.5 }}>
            Recargá la página para seguir trabajando. Si te vuelve a pasar, envianos el reporte:
            ya lleva el detalle técnico, no hace falta que escribas nada.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button type="button" style={btn(true)} onClick={() => window.location.reload()}>
              Recargar
            </button>
            <button type="button" style={btn(false)} onClick={this.sendReport}
                    disabled={sent === 'sending' || sent === 'ok'} data-testid="app-error-report">
              {sent === 'ok' ? 'Reporte enviado' : sent === 'sending' ? 'Enviando…' : 'Enviar reporte'}
            </button>
          </div>
          {sent === 'fail' && (
            <p style={{ margin: 0, color: '#fca5a5', fontSize: 14 }}>
              No pudimos enviar el reporte. Recargá y contalo desde «?» → «Reportar un problema».
            </p>
          )}
        </div>
      </div>
    );
  }
}
